import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Dashboard from '../../../layout/Dashboard'
import { Button, Card, Icon, Spinner } from '../../../components'
import { CupomModal } from '../Forms'
import { CouponRedeemSection } from '../CouponUi'
import { Api } from '../../../api/Assinafy'
import { getRedeemReceiveSummary, normalizeCouponCode } from '../../../utils/couponBenefits'

import '../styles.scss'
import './styles.scss'

const CouponRedeem = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isMountedRef = useRef(true)
  const autoApplyRef = useRef(false)

  const [modal, setModal] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [redeemStatus, setRedeemStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isAutoApplying, setIsAutoApplying] = useState(false)

  const handleValidateCoupon = async (couponCode) => {
    try {
      const normalizedCoupon = normalizeCouponCode(couponCode)
      const validateResponse = await Api('coupon/validate')(normalizedCoupon)

      if (validateResponse?.status !== 200) {
        return {
          success: false,
          message: validateResponse?.data?.message || 'Cupom inválido',
        }
      }

      const couponData = validateResponse?.data?.data || {}

      if (couponData.is_redeemable_on_plan_change_only) {
        return {
          success: false,
          message: 'Este cupom só pode ser usado na contratação de plano pago.',
        }
      }

      return {
        success: true,
        code: normalizedCoupon,
        coupon: couponData,
      }
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || 'Não foi possível validar o cupom',
      }
    }
  }

  const applyCouponFromCode = useCallback(async (couponCode) => {
    if (!couponCode) return

    setIsAutoApplying(true)
    setErrorMessage(null)

    const result = await handleValidateCoupon(couponCode)

    if (!isMountedRef.current) return

    if (result.success) {
      setAppliedCoupon({
        ...(result.coupon || {}),
        code: result.code,
      })
    } else {
      setErrorMessage(result.message)
    }

    setIsAutoApplying(false)
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (autoApplyRef.current || appliedCoupon) return

    const codeFromUrl = searchParams.get('code')
    const codeFromState = location.state?.couponCode
    const initialCode = codeFromUrl || codeFromState

    if (initialCode) {
      autoApplyRef.current = true
      applyCouponFromCode(initialCode)
    }
  }, [searchParams, location.state, appliedCoupon, applyCouponFromCode])

  const handleConfirmCoupon = (coupon) => {
    setAppliedCoupon(coupon)
    setErrorMessage(null)
  }

  const handleRedeem = async () => {
    if (!appliedCoupon?.code || isProcessing) return

    try {
      setIsProcessing(true)
      setRedeemStatus('processing')
      setErrorMessage(null)

      const response = await Api('coupon/redeem')(appliedCoupon.code)

      if (!isMountedRef.current) return

      if (response?.status === 200) {
        setRedeemStatus('success')
      } else {
        throw new Error(response?.data?.message || 'Não foi possível resgatar o cupom')
      }
    } catch (error) {
      if (isMountedRef.current) {
        setRedeemStatus('failure')
        setErrorMessage(
          error?.response?.data?.message || error?.message || 'Não foi possível resgatar o cupom'
        )
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false)
      }
    }
  }

  const handleReturn = () => {
    if (redeemStatus === 'success') {
      navigate('/dashboard/workspace/plan')
      return
    }

    setRedeemStatus(null)
    setErrorMessage(null)
  }

  if (redeemStatus === 'processing') {
    return (
      <Dashboard.Content style={{ maxWidth: '688px' }}>
        <div className="processing">
          <Spinner size={72} />
          <h1 className="processing-title">Resgatando cupom...</h1>
          <p className="processing-description">Estamos aplicando o benefício na sua conta, por favor aguarde.</p>
        </div>
      </Dashboard.Content>
    )
  }

  if (redeemStatus === 'success') {
    return (
      <Dashboard.Content style={{ maxWidth: '688px' }}>
        <div className="processing">
          <Icon id="icon-check" size={72} className="icon-success" />
          <h1 className="processing-title">Cupom resgatado!</h1>
          <p className="processing-description">
            Você recebeu <span className="fw-500">{getRedeemReceiveSummary(appliedCoupon)}</span>.
          </p>
          <Button onClick={handleReturn} variant="primary" className="processing-button">
            Ir para o plano
          </Button>
        </div>
      </Dashboard.Content>
    )
  }

  if (redeemStatus === 'failure') {
    return (
      <Dashboard.Content style={{ maxWidth: '688px' }}>
        <div className="processing">
          <Icon id="icon-close" size={72} className="icon-denied" />
          <h1 className="processing-title">Não foi possível resgatar</h1>
          <p className="processing-description">{errorMessage}</p>
          <Button onClick={handleReturn} variant="primary" className="processing-button">
            Tentar novamente
          </Button>
        </div>
      </Dashboard.Content>
    )
  }

  return (
    <Dashboard.Content style={{ maxWidth: '688px' }}>
      <Dashboard.Header
        returnButton
        title="Resgatar cupom"
        className="d-flex reverse-row"
      />

      <Card className="coupon-redeem-card payment-card">
        <div className="coupon-redeem-card-header">
          <h2>Resgate seu cupom</h2>
          <p>Digite o código e receba o benefício na hora — sem custo.</p>
        </div>

        {isAutoApplying ? (
          <div className="coupon-redeem-loading">
            <Spinner size={32} />
            <p>Validando cupom...</p>
          </div>
        ) : (
          <CouponRedeemSection
            coupon={appliedCoupon}
            code={appliedCoupon?.code}
            onRemove={() => {
              setAppliedCoupon(null)
              setErrorMessage(null)
            }}
            onChange={() => setModal(true)}
          />
        )}

        {errorMessage && !isAutoApplying && (
          <p className="coupon-redeem-error">{errorMessage}</p>
        )}
      </Card>

      <div className="d-flex w-100 mt-3 justify-content-end">
        <Button
          variant="primary"
          className="text-uppercase btn-wide btn-responsive"
          onClick={handleRedeem}
          disabled={!appliedCoupon?.code || isProcessing || isAutoApplying}
        >
          Resgatar cupom
        </Button>
      </div>

      <CupomModal
        show={modal}
        onClose={() => setModal(false)}
        cupom={appliedCoupon?.code}
        context="redeem"
        onValidateCoupon={handleValidateCoupon}
        onConfirmCoupon={handleConfirmCoupon}
      />
    </Dashboard.Content>
  )
}

export default CouponRedeem
